import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, DocumentTextIcon, ExternalLinkIcon, FilterIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";
import useSWR from "swr";
import { getData, putData } from "../backend";
import { WorkflowContext, useModal } from "../contexts";
import useDebounce from "../hooks/useDebounce";
import useHubspotSearch from "../hooks/useHubspotSearch";
import useUser from "../hooks/useUser";
import { HubspotConfig, PipelineStage } from "../interfaces";
import { BLOCK_ICON_MAP, capitalize } from "../utils";
import LoadingModal from "./LoadingModal";
import SmallSelectField from "./SmallSelectField";
import SmallTextInputField from "./SmallTextInputField";
import ToggleButton from "./ToggleButton";
import WorkflowModalHubspotObjects from "./WorkflowModalHubspotObjects";
import WorkflowSelectModal from "./WorkflowSelectModal";
import WorkflowSourceConditionModal from "./WorkflowSourceConditionModal";
import WorkflowSourceFieldTableHubSpot from "./WorkflowSourceFieldTableHubSpot";

const PAGE_SIZE = 100;

function WorkflowSourceHubspot() {
  const { user, hasPermission } = useUser();
  const { openModal, closeModal } = useModal();

  const { t } = useTranslation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow, automationOptions, updateAutomationOptions } = workflowContext;

  const history = useHistory();
  const { url } = useRouteMatch();

  const { sourceSettingsPortalNode: portalTarget } = useContext(WorkflowContext);

  const source = workflow.source;

  const primaryObjectTypeId = workflow.source?.hubspotConfig!.hubspotObjectTypeId;
  const showAddTemplate = workflow.documents.length === 0 && workflow.emails.length === 0;

  const [sortingField, setSortingField] = useState<string | undefined>(undefined);
  const [sortingDirection, setSortingDirection] = useState<"ASC" | "DSC">("ASC");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const maxSecondaryColumns = Math.min(Math.floor((window.innerWidth - 1000) / 100), 5);

  const primaryProperty = workflow.source?.hubspotConfig?.hubspotPrimaryDisplayProperty ?? "hs_object_id";
  const secondaryProperties = (workflow.source?.hubspotConfig?.hubspotSecondaryDisplayProperties ?? []).slice(0, maxSecondaryColumns);

  const { data, isLoading } = useHubspotSearch({
    primaryObjectTypeId: primaryObjectTypeId!,
    search: debouncedSearch,
    properties: [primaryProperty, ...secondaryProperties],
    associations: [],
    after: (currentPage - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    sortingField: sortingField,
    sortingDirection: sortingDirection,
  });

  const { results, total } = data ?? { results: [], paging: {}, total: undefined };

  const [lastTotal, setLastTotal] = useState<number | undefined>(undefined);
  const nPages = lastTotal ? Math.ceil(lastTotal / PAGE_SIZE) : 0;

  const { data: properties } = useSWR(`/hubspot/object-types/${primaryObjectTypeId}/properties/`, getData);
  const { data: pipelines } = useSWR(primaryObjectTypeId === "0-3" ? `/hubspot/pipelines/` : null, getData);

  const columnWidth = {
    0: "w-full",
    1: "w-1/2",
    2: "w-1/3",
    3: "w-1/4",
    4: "w-1/5",
  }[secondaryProperties.length];

  const dealStageMap = useMemo(() => {
    let map: Record<string, PipelineStage> = {};

    for (const pipeline of pipelines ?? []) {
      for (const stage of pipeline.stages)
        map[stage.id] = stage;
    }

    return map;
  }, [pipelines]);

  const propertyMap = useMemo(() => {
    let map: Record<string, any> = {};
    for (const property of properties?.results ?? [])
      map[property.name] = property;
    return map;
  }, [properties]);

  useEffect(() => {
    setSearch("");
    setCurrentPage(1);
    setSortingField(undefined);
    setLastTotal(undefined);
  }, [primaryObjectTypeId]);

  useEffect(() => {
    if (total === undefined)
      return;

    setLastTotal(total);
  }, [total]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);


  function handleToggleObjectSelected(objectId: string) {
    const current = !!(automationOptions.sourceItemIds?.includes(objectId) ?? false);

    let sourceItemIds: Array<string> = [];
    if (current)
      sourceItemIds = automationOptions.sourceItemIds?.filter((id: string) => !objectId) ?? [];
    else
      sourceItemIds = [...automationOptions.sourceItemIds ?? [], objectId];

    const selectedTotal = sourceItemIds.length;
    updateAutomationOptions({ sourceItemIds, selectedTotal, mode: "SOURCE_ITEM_IDS" });
  }

  function toggleHubspotOptions(options: Partial<HubspotConfig>) {
    const updatedConfig = { ...source!.hubspotConfig!, ...options };
    mutateWorkflow({ ...workflow, source: { ...workflow.source!, hubspotConfig: updatedConfig } }, { revalidate: false });

    setIsRefreshing(true);
    putData(`/hubspot/config/${source!.hubspotConfig!.id}/`, updatedConfig)
      .then(() => mutateWorkflow())
      .catch(console.error)
      .finally(() => setIsRefreshing(false));
  }

  function onSelectHubspotObjectType(hubspotObjectTypeId: string) {
    openModal(<LoadingModal title="Updating HubSpot Object Type" subtitle="Please wait while the object type is updated..." />);

    const updatedConfig = { ...source?.hubspotConfig!, hubspotObjectTypeId };
    putData(`/hubspot/config/${source!.hubspotConfig!.id}/`, updatedConfig)
      .then(() => mutateWorkflow())
      .catch(console.error)
      .finally(() => closeModal());
  }

  function handleSelectSortingProperty(property: string) {
    if (property === sortingField)
      setSortingDirection(sortingDirection === "ASC" ? "DSC" : "ASC");
    else
      setSortingDirection("ASC");

    setSortingField(property);
    setCurrentPage(1);
  }

  function formatProperty(propertyName: string, value: string) {
    const property = propertyMap[propertyName];
    if (!property)
      return String(value);

    if (property.name === "dealstage" && property.type === "enumeration" && dealStageMap)
      return dealStageMap[value]?.label ?? value;

    if (property.type === "date" && value)
      return new Date(value).toLocaleDateString();

    if (property.type === "datetime" && value)
      return new Date(value).toLocaleString();

    if (property.type === "boolean")
      return value === "true" ? "Yes" : "No";

    if (property.type === "number")
      return Number(value).toLocaleString();

    if (property.type === "enumeration" && property.options) {
      const option = property.options.find((o: any) => o.value === value);
      return option ? option.label : value;
    }

    if (value == null)
      return "---";

    return String(value);
  }

  if (!source)
    return null;

  const hubspotPortalId = workflow.team?.hubspotCredentials?.hubspotPortalId || user?.hubspotCredentials?.hubspotPortalId;

  return (
    <>
      {portalTarget && hasPermission(workflow.team, "workflows.edit") &&
        createPortal(
          <>
            <div className="flex flex-col">
              <div className="font-semibold text-xs text-gray-400">
                Selected Object Type
              </div>
              <div className="flex items-center gap-4">
                <div className="font-semibold text-gray-600">
                  {source.hubspotConfig?.hubspotObjectTypeName}
                </div>
                <div className="text-xs underline cursor-pointer ml-auto" onClick={() => openModal(<WorkflowModalHubspotObjects onSelect={onSelectHubspotObjectType} />)}>
                  Change
                </div>
              </div>
            </div>
            {!showAddTemplate &&
              <>
                <WorkflowSourceFieldTableHubSpot workflow={workflow} mutateWorkflow={mutateWorkflow} showRefreshSpinning={isRefreshing} disableCopy />
                <div className="flex flex-col">
                  <div className="flex items-center font-gilroy font-semibold text-gray-600">
                    <FilterIcon className="w-4 h-4 mr-2" /> {t("Source Filter Conditions")}
                  </div>
                  <div className="flex items-center font-gilroy font-semibold text-sm text-gray-400">
                    {t("Only process data that meets certain conditions")}
                  </div>
                  {(source!.sourceConditions.length > 0) &&
                    <div className="flex items-center font-gilroy font-semibold text-sm text-gray-600">
                      <div><Trans i18nKey="Filtering by <bold>{{count}}</bold> condition" count={source!.sourceConditions.length} components={{ bold: <span className="font-semibold" /> }} /></div>
                    </div>
                  }
                  <button className="btn btn-white text-sm w-52 mt-2" onClick={() => openModal(<WorkflowSourceConditionModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                    {source!.sourceConditions.length === 0 ? t("Add Conditions") : t("Edit Conditions")}
                  </button>
                </div>
                <div className='flex flex-col gap-1'>
                  <div className='text-sm font-semibold font-gilroy text-gray-600'>
                    {t("Show in HubSpot")}
                  </div>
                  <div className='flex items-center gap-2'>
                    <ToggleButton value={!!source?.hubspotConfig?.showInHubspot} onChange={showInHubspot => toggleHubspotOptions({ showInHubspot })} />
                    <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                      {t("Show this workflow in the 'Run Workflow' actions menu inside HubSpot")}
                    </div>
                  </div>
                </div>
                <div className='flex flex-col gap-1'>
                  <div className='text-sm font-semibold font-gilroy text-gray-600'>
                    {t("Run from Associated Objects (Advanced)")}
                  </div>
                  <div className='flex items-center gap-2'>
                    <ToggleButton value={!!source?.hubspotConfig?.runFromSecondaryObjects} onChange={runFromSecondaryObjects => toggleHubspotOptions({ runFromSecondaryObjects })} />
                    <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                      {t("Allow this workflow to be ran from associated objects in HubSpot")}
                    </div>
                  </div>
                </div>
              </>
            }

            {showAddTemplate &&
              <>
                <div className="flex flex-col gap-4 justify-center">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-6 h-6 mr-2" />
                    <div className="font-gilroy font-semibold text-lg">
                      {t("Connect a Template")}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 font-semibold text-gray-600">
                    {t("Create documents and presentations from a template")}
                  </div>
                  <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Template")} subtitle={t("Create or select a template to merge your data with")} showDocuments workflowContext={workflowContext} />)}>
                    {t("Connect")}
                  </button>
                </div>
              </>
            }
          </>,
          portalTarget
        )
      }

      <Transition.Child className="flex flex-col bg-white w-full m-4 p-4 pt-2 shadow rounded"
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex w-full items-center">
          <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
            <img className="h-10 rounded-md" src={BLOCK_ICON_MAP["HUBSPOT"]} alt="HubSpot Icon" />
          </div>
          <div className="flex flex-col h-full justify-center">
            <div className="text-gray-400 font-gilroy font-semibold text-sm">
              {t("External Data Source")}
            </div>
            <div className="flex items-center w-full font-gilroy font-semibold text-gray-600 cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(`https://app.hubspot.com/contacts/${hubspotPortalId}/objects/${primaryObjectTypeId}`, "_blank")}>
              HubSpot {capitalize(source.hubspotConfig?.hubspotObjectTypeName ?? "")}
              <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1" />
            </div>
          </div>
          <XIcon className="w-6 h-6 ml-auto text-gray-400 text-xs hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        </div>

        <div className="flex flex-col mt-2 overflow-hidden border border-gray-200 rounded h-full">
          {lastTotal !== undefined &&
            <>
              <div className="flex items-baseline px-4 py-2 pt-3 border-b">
                <SmallTextInputField value={search} onChange={setSearch} placeholder="Search..." className="w-80" disabled={lastTotal === 0 && search.length === 0} />
                <div className="mr-auto flex-shrink" />

                {(automationOptions.selectedTotal ?? 0) > 0 &&
                  <>
                    <div className="font-semibold text-blue text-sm ml-2">
                      {automationOptions.selectedTotal} Selected
                    </div>
                    <div className="font-semibold text-gray-400 underline hover:text-gray-600 text-sm ml-2 cursor-pointer" onClick={() => updateAutomationOptions({ sourceItemIds: [], selectedTotal: 0 })}>
                      Clear
                    </div>
                  </>
                }
                <div className="font-semibold text-gray-400 text-sm ml-auto">
                  Total Results:
                </div>
                <div className="font-semibold text-gray-600 text-sm ml-2">
                  {lastTotal}
                </div>
                <div className="font-semibold text-gray-400 text-sm ml-4">
                  Page:
                </div>
                <SmallSelectField value={String(currentPage)} onChange={value => setCurrentPage(Number(value))} className="ml-2 w-12" disabled={nPages <= 1}>
                  {Array.from({ length: (nPages || 1) }, (_, i) => i + 1).map(page => <option key={page} value={page}>{page}</option>)}
                </SmallSelectField>
              </div>

              <div className="flex items-center gap-4 border-b border-gray-200 bg-gray-100 px-4 pt-2 pb-1 font-semibold text-sm text-gray-600 overflow-y-scroll flex-shrink-0 scrollbar-invisible">
                <input type="checkbox" className="mr-4 invisible" disabled />
                {[primaryProperty ?? "hs_object_id", ...secondaryProperties].map(property => (
                  <div key={property} className={`flex items-center ${columnWidth} cursor-pointer`} onClick={() => handleSelectSortingProperty(property)}>
                    {propertyMap?.[property]?.["label"] ?? property}
                    {sortingField === property && sortingDirection === "ASC" &&
                      <ChevronDownIcon className="w-4 h-4 ml-1 -mt-1" />
                    }
                    {sortingField === property && sortingDirection === "DSC" &&
                      <ChevronUpIcon className="w-4 h-4 ml-1 -mt-1" />
                    }
                  </div>
                ))}
              </div>
            </>
          }

          <div className="overflow-y-scroll h-full">
            {isLoading &&
              <div className="h-full flex items-center justify-center">
                <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24" alt="Loading Logo" />
              </div>
            }

            {total === 0 &&
              <div className="h-full flex items-center justify-center text-gray-400 font-semibold">
                No results were found
              </div>
            }

            {results?.map(object => (
              <div key={object.id} className={`flex flex-col border-b last:border-0 px-4 py-4 pb-1 ${automationOptions.sourceItemIds?.includes(object.id) ? "bg-blue-50" : "bg-white"}`} onClick={() => handleToggleObjectSelected(object.id)}>
                <div className="flex items-center gap-4">
                  <input type="checkbox" className="mr-4" checked={!!(automationOptions?.sourceItemIds?.includes(object.id) ?? false)} onChange={e => handleToggleObjectSelected(object.id)} />
                  <a className={`flex font-semibold text-gray-600 group hover:underline ${columnWidth} overflow-hidden flex-grow-0`} href={`https://app.hubspot.com/contacts/${hubspotPortalId}/record/${primaryObjectTypeId}/${object.id}/`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                    <span className="truncate inline-block" style={{ "maxWidth": "calc(100% - 30px)" }}>
                      {object.properties[primaryProperty ?? ""] ?? object.id}
                    </span>
                    {/* {source.hubspotConfig?.hubspotObjectTypeId === "0-1" && " " + object.properties["lastname"]} */}
                    <ExternalLinkIcon className="h-4 w-4 ml-1 text-blue invisible group-hover:visible inline-block" />
                  </a>
                  {secondaryProperties.map(property => (
                    <div key={property} className={`${columnWidth} truncate`}>
                      {formatProperty(property, object.properties[property]) ?? "---"}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Transition.Child>
    </>
  );

}

export default WorkflowSourceHubspot;
